import { useEffect } from 'react';
import type { MutableRefObject } from 'react';
import type Handsontable from 'handsontable/base';

// Utils
import { getTableCellEditorId, getTableGridEditorId } from '../utils/tableEditorSessionUtils';

/**
 * Custom hook for managing table selection side effects.
 */
export const useTableSelectionEffects = ({
    elementId,
    currentlyEditingEditorId,
    hotTableInstanceRef,
    isSingleSelected,
    isResizing,
    isReadOnly,
}: {
    elementId: string;
    currentlyEditingEditorId?: string | null;
    hotTableInstanceRef: MutableRefObject<Handsontable | null>;
    isSingleSelected: boolean;
    isResizing: boolean;
    isReadOnly: boolean;
}): void => {
    useEffect(() => {
        // Prevent cells from reselecting if another element is selected or if user is editing title/caption
        // this is just because on first click onto another element the table rerenders before meta is cleared
        // from app selection state
        const hotInstance = hotTableInstanceRef.current;
        if (!hotInstance || isResizing) return;

        if (!isSingleSelected) {
            hotInstance.deselectCell();
            hotInstance.unlisten();
        }

        // Start listening when the table is single selected so that handsontable's shortcuts work
        if (isSingleSelected && !isReadOnly) hotInstance.listen();
    }, [hotTableInstanceRef, isReadOnly, isResizing, isSingleSelected]);

    /**
     * If user focuses on another editor key on the table (e.g. title/caption), deselect the current cell selection.
     */
    useEffect(() => {
        const hotInstance = hotTableInstanceRef.current;
        if (!hotInstance || !hotInstance.isListening()) return;

        const isEditingCellOrGrid =
            !currentlyEditingEditorId ||
            currentlyEditingEditorId === getTableGridEditorId(elementId) ||
            currentlyEditingEditorId === getTableCellEditorId(elementId);

        if (isEditingCellOrGrid) return;

        hotInstance.deselectCell();
        hotInstance.unlisten();
    }, [elementId, currentlyEditingEditorId, hotTableInstanceRef]);
};

export default useTableSelectionEffects;
